import { useEffect, useState } from 'react'

import { ApiPortfolioStatus } from '@/api'
import {
  DownloadLink,
  Icon,
  LinkIcon,
  MessageBox,
  Space,
  StateCircle,
  Typography
} from '@/components/core-v2'
import { onDownloadFactSheetClick, onDownloadPortfolioExcelClick } from '@/utilities'
import { useUser } from '@/utilities/hooks/useUser'

import { inactiveStatuses, PortfolioStatus } from '../../constants'
import { useAppSelector } from '../../store/hooks'
import {
  calculateApprovalMessageBoxContent,
  calculateApprovalTagContent,
  portfolioApprovalStatusCircleColorMap
} from '../../utils'
import { ApprovalStatusTag } from '../approval-status-tag'
import { ApprovalModal, ProposedChangesModal } from '../proposed-changes-modal'

export const PortfolioDetailsHeader = ({
  approvalStatus,
  canProceedApproval,
  isFundOfFund,
  isNew,
  onApprovePortfolio,
  onChangeApprovalStatus,
  onChangeApprovalUserName,
  portfolioId,
  portfolioName,
  resetApprovalStatus
}: {
  approvalStatus: ApiPortfolioStatus
  canProceedApproval: boolean
  isFundOfFund: boolean
  isNew: boolean
  onApprovePortfolio: () => void
  onChangeApprovalStatus: (status: 'Approved' | 'Rejected' | '') => void
  onChangeApprovalUserName: (userName: string) => void
  portfolioId: string
  portfolioName: string
  resetApprovalStatus: () => void
}) => {
  const { data: user } = useUser()
  const [modalIsOpen, setModalIsOpen] = useState(false)

  const state = useAppSelector(state => state.editPortfolios)

  const { availableStrategies, proposedPortfolio, selectedPortfolio } = state

  const tagContent = calculateApprovalTagContent(approvalStatus)
  const messageBoxContent = calculateApprovalMessageBoxContent(
    tagContent.text,
    selectedPortfolio.type
  )
  const statusName = inactiveStatuses.includes(selectedPortfolio.status)
    ? PortfolioStatus.NEW
    : PortfolioStatus.ACTIVE

  useEffect(() => {
    if (state.saveSuccess) {
      setModalIsOpen(false)
    }
  }, [state.saveSuccess])

  const onCloseModal = () => {
    resetApprovalStatus()
    setModalIsOpen(false)
  }

  const onOpenModal = () => {
    resetApprovalStatus()
    setModalIsOpen(true)
  }

  return (
    <>
      <Space isflex alignItems="center" justifyContent="space-between" mb={5}>
        <Space isflex alignItems="center">
          <Space mr={5}>
            <Typography fontSize="15px" fontWeight="bold" variant="b3">
              {portfolioName}
            </Typography>
          </Space>
          <Typography fontSize="13px" fontWeight="normal" variant="b3">
            {isFundOfFund ? '(Fund of funds)' : '(Model)'}
          </Typography>
        </Space>
        <Space isflex alignItems="center">
          <DownloadLink onClick={() => onDownloadFactSheetClick(portfolioId)}>
            Portfolio Factsheet.pdf
          </DownloadLink>
          <Space ml={4}>
            <DownloadLink onClick={() => onDownloadPortfolioExcelClick(portfolioId, portfolioName)}>
              Daily performance data.xlsx
            </DownloadLink>
          </Space>
        </Space>
      </Space>
      {!isNew && (
        <Space isflex alignItems="center" justifyContent="space-between">
          <Space mb={1} mr={4} mt={1}>
            <Space isflex alignItems="center" flexDirection="row">
              <StateCircle state={portfolioApprovalStatusCircleColorMap[statusName]} />
              <div>{statusName}</div>
            </Space>
          </Space>
          <Space isflex alignItems="center">
            <>
              <ApprovalStatusTag tagContent={tagContent} />
              {selectedPortfolio.status === 'PendingApprovalNew' &&
                (user.roles?.includes('FLStructuring') ||
                  user.roles?.includes('Administrator')) && (
                  <>
                    <Space ml={4}>
                      <LinkIcon
                        disabled={false}
                        icon={<Icon name="search" size="default" />}
                        iconPosition="left"
                        text="Approve or reject this portfolio"
                        onClick={onOpenModal}
                      />
                    </Space>
                    {modalIsOpen && (
                      <ApprovalModal
                        canProceedApproval={canProceedApproval}
                        portfolio={selectedPortfolio as any}
                        onApprovePortfolio={onApprovePortfolio}
                        onChangeApprovalStatus={onChangeApprovalStatus}
                        onChangeApprovalUserName={onChangeApprovalUserName}
                        onClose={onCloseModal}
                      />
                    )}
                  </>
                )}
              {selectedPortfolio.status === 'PendingApprovalEdit' && (
                <>
                  <Space ml={4}>
                    <LinkIcon
                      disabled={false}
                      icon={<Icon name="search" size="default" />}
                      iconPosition="left"
                      text={
                        user.roles?.includes('FLStructuring')
                          ? 'Approve or reject this portfolio'
                          : 'Review proposed changes'
                      }
                      onClick={onOpenModal}
                    />
                  </Space>
                  {modalIsOpen && (
                    <ProposedChangesModal
                      availableStrategies={availableStrategies}
                      canProceedApproval={canProceedApproval}
                      currentPortfolio={selectedPortfolio as any}
                      currentTotalValue={state.selectedPortfolioUsage?.totalValue}
                      proposedPortfolio={proposedPortfolio as any}
                      onApprovePortfolio={onApprovePortfolio}
                      onChangeApprovalStatus={onChangeApprovalStatus}
                      onChangeApprovalUserName={onChangeApprovalUserName}
                      onClose={onCloseModal}
                    />
                  )}
                </>
              )}
            </>
          </Space>
        </Space>
      )}
      {messageBoxContent.title && (
        <Space mt={6}>
          <MessageBox state="error">
            <Space isflex>
              <Space mr={3}>
                <Icon name="circleWarning" size="default" />
              </Space>
              <Space isflex flexDirection="column">
                <Space mb={3}>
                  <Typography fontSize="15px" fontWeight="bold" variant="b3">
                    {messageBoxContent.title}
                  </Typography>
                </Space>
                <Space>
                  <Typography fontSize="15px" fontWeight="normal" variant="b3">
                    {messageBoxContent.description}
                  </Typography>
                </Space>
              </Space>
            </Space>
          </MessageBox>
        </Space>
      )}
    </>
  )
}
